"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { toFilterDate } from "@/features/auctions/v2/mine/filterDate";
import { useCurrencies } from "@/features/currencies/hooks/useCurrencies";
import { useProfile } from "@/features/profile/hooks/useProfile";
import { useDebounce } from "@/hooks/useDebounce";
import { formatTokenSymbol } from "@/lib/asset-naming";
import { getBids } from "../../api";
import type { BidItem } from "../../types";

/* eslint-disable @typescript-eslint/no-explicit-any -- the currencies payload is untyped, as it was in MyBidsClient */

export const MY_BIDS_PAGE = 12;
export type BidStatusTab = "all" | "pending" | "accepted" | "rejected" | "won";
export type BidSortValue = "newest" | "oldest" | "highest" | "lowest";
const stamp = (bid: BidItem) => {
  const t = new Date(bid.created_at ?? "").getTime();
  return Number.isNaN(t) ? 0 : t;
};

/** MyBidsClient's order, unchanged — over what is loaded; `/bids` has no sort param. */
export function sortBidItems(items: BidItem[], sortBy: BidSortValue) {
  return [...items].sort((a, b) => {
    if (sortBy === "highest") return Number(b.price) - Number(a.price);
    if (sortBy === "lowest") return Number(a.price) - Number(b.price);
    if (sortBy === "oldest") return stamp(a) - stamp(b);
    return stamp(b) - stamp(a);
  });
}

/**
 * THE VIEWER'S BIDS — MyBidsClient's rules, moved here unchanged:
 * `/bids?user_id=<me>` (the id from the profile), the auction id (digits
 * only, debounced 300ms), currency, the winning state and two dates. `/bids`
 * filters only `is_win` on the server: the "won" view rides it (and then
 * overrides the winning-state choice), while pending / accepted / rejected
 * narrow what is loaded, and the order reorders what is loaded. Counts the
 * server knows (the total of the active all/won query) are shown; the others
 * are never guessed. The one difference is the paging: twelve at a time as
 * the reader scrolls instead of numbered pages.
 */
export function useMyBids(lang) {
  const $ = _c(68);
  const [auctionId, setAuctionIdRaw] = useState("");
  const [currencyId, setCurrencyId] = useState("");
  const [winnerFilter, setWinnerFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [tab, setTab] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const debouncedAuctionId = useDebounce(auctionId, 300);
  const {
    data: profileData,
    isLoading: isProfileLoading
  } = useProfile();
  const {
    data: currenciesData
  } = useCurrencies();
  const userId = profileData?.data?.user?.id;
  const effectiveIsWin = tab === "won" ? true : winnerFilter === "" ? undefined : winnerFilter === "true";
  const t0 = debouncedAuctionId ? Number(debouncedAuctionId) : undefined;
  const t1 = currencyId ? Number(currencyId) : undefined;
  let t2;
  if ($[0] !== fromDate) {
    t2 = toFilterDate(fromDate);
    $[0] = fromDate;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== toDate) {
    t3 = toFilterDate(toDate);
    $[2] = toDate;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== effectiveIsWin || $[5] !== lang || $[6] !== t0 || $[7] !== t1 || $[8] !== t2 || $[9] !== t3 || $[10] !== userId) {
    t4 = {
      user_id: userId,
      auction_id: t0,
      is_win: effectiveIsWin,
      currency_id: t1,
      from: t2,
      to: t3,
      limit: MY_BIDS_PAGE,
      lang
    };
    $[4] = effectiveIsWin;
    $[5] = lang;
    $[6] = t0;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = userId;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const params = t4;
  let t5;
  if ($[12] !== params) {
    t5 = ["bids", "mine", params];
    $[12] = params;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== params) {
    t6 = t7 => {
      const {
        pageParam
      } = t7;
      return getBids({
        ...params,
        page: pageParam
      });
    };
    $[14] = params;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const t7 = Boolean(userId);
  let t8;
  if ($[16] !== t5 || $[17] !== t6 || $[18] !== t7) {
    t8 = {
      queryKey: t5,
      initialPageParam: 1,
      queryFn: t6,
      getNextPageParam: _temp,
      enabled: t7,
      staleTime: 30000
    };
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const query = useInfiniteQuery(t8);
  let out;
  if ($[20] !== query.data?.pages) {
    const seen = new Set();
    out = [];
    for (const p of query.data?.pages ?? []) {
      for (const b of p.data.items) {
        if (!seen.has(b.id)) {
          seen.add(b.id);
          out.push(b);
        }
      }
    }
    $[20] = query.data?.pages;
    $[21] = out;
  } else {
    out = $[21];
  }
  const bids = out;
  const total = query.data?.pages[0]?.data.page.total ?? bids.length;
  const t9 = tab === "won" ? null : total;
  const t10 = tab === "won" ? total : null;
  let t11;
  if ($[22] !== t10 || $[23] !== t9) {
    t11 = {
      all: t9,
      won: t10,
      pending: null,
      accepted: null,
      rejected: null
    };
    $[22] = t10;
    $[23] = t9;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  const counts = t11;
  const narrowed = tab === "pending" || tab === "accepted" || tab === "rejected";
  let t12;
  if ($[25] !== bids || $[26] !== narrowed || $[27] !== tab) {
    t12 = narrowed ? bids.filter(b_0 => b_0.status === tab) : bids;
    $[25] = bids;
    $[26] = narrowed;
    $[27] = tab;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== sortBy || $[30] !== t12) {
    t13 = sortBidItems(t12, sortBy);
    $[29] = sortBy;
    $[30] = t12;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  const visible = t13;
  let t14;
  if ($[32] !== visible) {
    t14 = visible.reduce(_temp2, {
      total: 0,
      pending: 0,
      accepted: 0,
      rejected: 0,
      won: 0
    });
    $[32] = visible;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  const stats = t14;
  let t15;
  if ($[34] !== currenciesData?.data?.currencies) {
    t15 = currenciesData?.data?.currencies ?? [];
    $[34] = currenciesData?.data?.currencies;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== t15) {
    t16 = t15.map(_temp3);
    $[36] = t15;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  const currencies = t16 as {
    key: string;
    label: string;
  }[];
  let t17;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = () => {
      setAuctionIdRaw("");
      setCurrencyId("");
      setWinnerFilter("");
      setFromDate("");
      setToDate("");
      setTab("all");
      setSortBy("newest");
    };
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  const reset = t17;
  const filtered = Boolean(auctionId || currencyId || winnerFilter || fromDate || toDate || tab !== "all" || sortBy !== "newest");
  let t18;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = v => setAuctionIdRaw(v.replace(/[^\d]/g, ""));
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== auctionId || $[41] !== currencyId || $[42] !== filtered || $[43] !== fromDate || $[44] !== sortBy || $[45] !== tab || $[46] !== toDate || $[47] !== winnerFilter) {
    t19 = {
      auctionId,
      setAuctionId: t18,
      currencyId,
      setCurrencyId,
      winnerFilter,
      setWinnerFilter,
      fromDate,
      setFromDate,
      toDate,
      setToDate,
      tab,
      setTab,
      sortBy,
      setSortBy,
      reset,
      filtered
    };
    $[40] = auctionId;
    $[41] = currencyId;
    $[42] = filtered;
    $[43] = fromDate;
    $[44] = sortBy;
    $[45] = tab;
    $[46] = toDate;
    $[47] = winnerFilter;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  const t20 = isProfileLoading || !userId || query.isPending;
  const t21 = query.isError && bids.length === 0;
  const t22 = query.isFetching && !query.isFetchingNextPage && !query.isPending;
  const t23 = Boolean(query.hasNextPage);
  let t24;
  let t25;
  if ($[49] !== query) {
    t24 = () => {
      query.fetchNextPage();
    };
    t25 = () => {
      query.refetch();
    };
    $[49] = query;
    $[50] = t24;
    $[51] = t25;
  } else {
    t24 = $[50];
    t25 = $[51];
  }
  let t26;
  if ($[52] !== bids || $[53] !== counts || $[54] !== currencies || $[55] !== narrowed || $[56] !== query.isFetchingNextPage || $[57] !== stats || $[58] !== t19 || $[59] !== t20 || $[60] !== t21 || $[61] !== t22 || $[62] !== t23 || $[63] !== t24 || $[64] !== t25 || $[65] !== total || $[66] !== visible) {
    t26 = {
      filters: t19,
      currencies,
      bids,
      visible,
      stats,
      counts,
      total,
      narrowed,
      isLoading: t20,
      isError: t21,
      isRefreshing: t22,
      canFetchMore: t23,
      isFetchingMore: query.isFetchingNextPage,
      fetchMore: t24,
      retry: t25
    };
    $[52] = bids;
    $[53] = counts;
    $[54] = currencies;
    $[55] = narrowed;
    $[56] = query.isFetchingNextPage;
    $[57] = stats;
    $[58] = t19;
    $[59] = t20;
    $[60] = t21;
    $[61] = t22;
    $[62] = t23;
    $[63] = t24;
    $[64] = t25;
    $[65] = total;
    $[66] = visible;
    $[67] = t26;
  } else {
    t26 = $[67];
  }
  return t26;
}
function _temp3(c) {
  return {
    key: String(c.id),
    label: `${c.name} (${formatTokenSymbol(c.symbol)})`
  };
}
function _temp2(acc, b_1) {
  acc.total = acc.total + 1;
  if (b_1.status === "pending") {
    acc.pending = acc.pending + 1;
  }
  if (b_1.status === "accepted") {
    acc.accepted = acc.accepted + 1;
  }
  if (b_1.status === "rejected") {
    acc.rejected = acc.rejected + 1;
  }
  if (b_1.is_win) {
    acc.won = acc.won + 1;
  }
  return acc;
}
function _temp(last) {
  return last.data.page.current_page < last.data.page.last_page ? last.data.page.current_page + 1 : undefined;
}